import { extractKeyValuePairs } from "./util";

/**
 * Split a search string into its keyed terms (`status:OVERDUE`) and the
 * leftover free text.
 *
 * This lives in its own module — rather than alongside the other helpers in
 * `searchTerms.ts` — because it is the one export Oak needs.
 *
 * Oak deep-imports `encamp-omnisearch/src/...` directly, so everything a module
 * it imports pulls in lands in the browser module graph. Vite does not
 * pre-bundle linked workspace packages, so it serves `encamp-shared/dist/*` as
 * raw CommonJS and an ESM `import { safeParseInt }` against it throws "does not
 * provide an export named" at load time, white-screening the whole app —
 * login page included. Keeping this export free of cross-package imports lets
 * `searchTerms.ts` use `encamp-shared` normally.
 *
 * Keep this file's imports limited to other browser-safe `encamp-omnisearch`
 * modules.
 */
export function extractKeyedSearchTerms(
  search: string
): [{ key: string; value: string[] }[], string] {
  const keyValuePairsWithPositions = extractKeyValuePairs(search);

  const keyValuePairs = keyValuePairsWithPositions.map(({ key, value }) => ({
    key,
    value,
  }));

  // Construct the unmatched portion by excluding matched segments
  let unmatchedSearch = "";
  let currentIndex = 0;

  keyValuePairsWithPositions.forEach(({ start, end }) => {
    if (currentIndex < start) {
      unmatchedSearch += search.slice(currentIndex, start) + " ";
    }
    currentIndex = end;
  });

  // Append any remaining unmatched portion after the last match
  if (currentIndex < search.length) {
    unmatchedSearch += search.slice(currentIndex);
  }

  return [keyValuePairs, unmatchedSearch.trim()];
}
